import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import PageContainer from '@/components/layout/PageContainer'
import { Card, MetricCard, StatusBadge, ActionButton, LoadingSpinner } from '@/components/common'
import { AIInsightPanel } from '@/components/insights'
import { TimeSeriesChart } from '@/components/charts'
import { SmartCompressorCostCalculator } from '@/components/reports'
// narrativeGenerator replaced with inline useMemo narratives
import { useUIStore, useAssetStore } from '@/store'
import { get as apiGet } from '@/services/api'
import { ENDPOINTS } from '@/services/endpoints'
import { getMaintenanceEfficiencyWithFallback, MaintenanceEfficiencyData, getPartsPredictionWithFallback, PartsPredictionResponse } from '@/services/dashboardApi'
import { COST_DEFAULTS } from '@/constants/thresholds'
import { Calculator, RefreshCw } from 'lucide-react'
import clsx from 'clsx'

// Types
interface MaintenanceItem {
  asset: string
  action: string
  priority: string
  cost: number
  savings: number
  date: string
}

interface PartsPredictionItem {
  part: string
  partId?: string
  category?: string
  quantity: number
  confidence: number
  leadTime: string
  urgency?: 'high' | 'medium' | 'low'
  unitCost?: number
  totalCost?: number
  recommendation?: string
}

interface MaintenanceSchedulerResponse {
  // New backend format
  asset_id?: string
  scheduled_maintenance?: Array<{
    task: string
    scheduled_date: string
    priority: string
    estimated_duration_hours: number
  }>
  next_maintenance?: {
    task: string
    scheduled_date: string
    priority: string
    estimated_duration_hours: number
  }
  overdue_count?: number
  // Legacy format (kept for compatibility)
  recommendations?: Array<{
    asset_id: string
    action: string
    priority: string
    estimated_cost: number
    prevented_loss: number
    deadline: string
    confidence: number
  }>
  parts_prediction?: Array<{
    part_name: string
    quantity: number
    confidence: number
    lead_time: string
  }>
  summary?: {
    total_actions: number
    critical_count: number
    total_cost: number
    total_savings: number
  }
  timestamp: string
}

// Default mock data - used only as fallback when API is unavailable
// NOTE: Cost/savings values should come from API based on actual equipment data
// These are representative examples based on industry averages
const defaultMaintenanceSchedule: MaintenanceItem[] = [
  { asset: 'F67503', action: 'Valve inspection', priority: 'critical', cost: 2500, savings: 45000, date: 'Today' },
  { asset: 'F69871', action: 'Seal replacement', priority: 'high', cost: 3500, savings: 22000, date: 'Tomorrow' },
  { asset: 'F70478', action: 'Calibration', priority: 'medium', cost: 800, savings: 5000, date: 'This week' },
  { asset: 'F68885', action: 'Bearing inspection', priority: 'medium', cost: 1200, savings: 8000, date: 'This week' },
  { asset: 'F70231', action: 'Oil analysis', priority: 'low', cost: 500, savings: 2000, date: 'Next week' },
]

const defaultPartsPrediction: PartsPredictionItem[] = [
  { part: 'Suction Valve', partId: 'VALV-002', category: 'valves', quantity: 3, confidence: 92, leadTime: '2-3 days', urgency: 'high', unitCost: 875, totalCost: 2625, recommendation: 'Order now - 2 assets showing valve degradation' },
  { part: 'Piston Ring Set', partId: 'RING-001', category: 'rings', quantity: 2, confidence: 85, leadTime: '5-7 days', urgency: 'medium', unitCost: 1200, totalCost: 2400, recommendation: 'Order within 3 days based on wear patterns' },
  { part: 'Seal Kit', partId: 'SEAL-001', category: 'seals', quantity: 5, confidence: 88, leadTime: '1-2 days', urgency: 'high', unitCost: 450, totalCost: 2250, recommendation: 'Critical stock replenishment - high usage rate' },
  { part: 'Bearing Assembly', partId: 'BEAR-001', category: 'bearings', quantity: 1, confidence: 72, leadTime: '7-10 days', urgency: 'low', unitCost: 1850, totalCost: 1850, recommendation: 'Preemptive order for scheduled maintenance' },
]

export default function MaintenanceIntelligence() {
  const navigate = useNavigate()
  const { addNotification } = useUIStore()
  const { assets, fetchAssets } = useAssetStore()
  const [scheduledItems, setScheduledItems] = useState<Set<string>>(new Set())
  const [showCostCalculator, setShowCostCalculator] = useState(false)

  // Guard against assets not being an array
  const safeAssets = Array.isArray(assets) ? assets : []

  // Dynamic data state
  const [maintenanceSchedule, setMaintenanceSchedule] = useState<MaintenanceItem[]>(defaultMaintenanceSchedule)
  const [partsPrediction, setPartsPrediction] = useState<PartsPredictionItem[]>(defaultPartsPrediction)
  const [partsPredictionData, setPartsPredictionData] = useState<PartsPredictionResponse | null>(null)
  const [efficiencyTrend, setEfficiencyTrend] = useState<MaintenanceEfficiencyData | null>(null)
  const [loading, setLoading] = useState(true)
  const [usingMockData, setUsingMockData] = useState(false)

  // Calculate totals from dynamic data
  const totalCost = useMemo(() => maintenanceSchedule.reduce((sum, m) => sum + m.cost, 0), [maintenanceSchedule])
  const totalSavings = useMemo(() => maintenanceSchedule.reduce((sum, m) => sum + m.savings, 0), [maintenanceSchedule])
  const criticalCount = useMemo(() => maintenanceSchedule.filter(m => m.priority === 'critical').length, [maintenanceSchedule])

  // Fetch maintenance data from API
  const fetchMaintenanceData = useCallback(async () => {
    setLoading(true)
    setUsingMockData(false)

    try {
      // Fetch maintenance scheduler, efficiency trend, and parts prediction in parallel
      const [schedulerResult, efficiencyResult, partsResult] = await Promise.allSettled([
        (async () => {
          const targetAsset = safeAssets.find(a => a.status === 'critical') || safeAssets.find(a => a.status === 'warning') || safeAssets[0]
          if (targetAsset) {
            return apiGet<MaintenanceSchedulerResponse>(ENDPOINTS.DASHBOARD_MAINTENANCE_SCHEDULER(targetAsset.id))
          }
          return null
        })(),
        getMaintenanceEfficiencyWithFallback(6),
        getPartsPredictionWithFallback(30)
      ])

      // Process parts prediction from API
      if (partsResult.status === 'fulfilled' && partsResult.value) {
        const partsData = partsResult.value.data
        setPartsPredictionData(partsData)
        if (partsData.predictions && partsData.predictions.length > 0) {
          setPartsPrediction(partsData.predictions.map(p => ({
            part: p.part_name,
            partId: p.part_id,
            category: p.category,
            quantity: p.predicted_quantity,
            confidence: p.confidence,
            leadTime: `${p.lead_time_days} days`,
            urgency: p.urgency as 'high' | 'medium' | 'low',
            unitCost: p.unit_cost,
            totalCost: p.total_cost,
            recommendation: p.recommendation
          })))
          if (!partsResult.value.isMock) {
            setUsingMockData(false)
          }
        }
      }

      // Process scheduler data
      if (schedulerResult.status === 'fulfilled' && schedulerResult.value) {
        const data = schedulerResult.value

        // Handle new backend format with scheduled_maintenance
        if (data.scheduled_maintenance && data.scheduled_maintenance.length > 0) {
          // Helper to format date
          const formatDate = (isoDate: string) => {
            try {
              const date = new Date(isoDate)
              const now = new Date()
              const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
              if (diffDays <= 0) return 'Today'
              if (diffDays === 1) return 'Tomorrow'
              if (diffDays <= 7) return 'This week'
              if (diffDays <= 14) return 'Next week'
              return date.toLocaleDateString()
            } catch { return 'Scheduled' }
          }

          // Estimate costs based on priority and duration
          const estimateCost = (priority: string, hours: number) =>
            priority === 'high' ? Math.round(hours * 500) :
            priority === 'medium' ? Math.round(hours * 300) :
            Math.round(hours * 200)

          // Estimate savings (typically 10-20x cost for preventive maintenance)
          const estimateSavings = (cost: number, priority: string) =>
            priority === 'high' ? cost * 18 :
            priority === 'medium' ? cost * 12 :
            cost * 8

          setMaintenanceSchedule(data.scheduled_maintenance.map(m => {
            const cost = estimateCost(m.priority, m.estimated_duration_hours)
            return {
              asset: data.asset_id || 'Fleet',
              action: m.task,
              priority: m.priority === 'high' ? 'critical' : m.priority,
              cost,
              savings: estimateSavings(cost, m.priority),
              date: formatDate(m.scheduled_date)
            }
          }))
          // Parts prediction now comes from dedicated API endpoint above
          setUsingMockData(false)
        }
        // Handle legacy format with recommendations
        else if (data.recommendations && data.recommendations.length > 0) {
          setMaintenanceSchedule(data.recommendations.map(r => ({
            asset: r.asset_id,
            action: r.action,
            priority: r.priority,
            cost: r.estimated_cost,
            savings: r.prevented_loss,
            date: r.deadline
          })))

          if (data.parts_prediction && data.parts_prediction.length > 0) {
            setPartsPrediction(data.parts_prediction.map(p => ({
              part: p.part_name,
              quantity: p.quantity,
              confidence: p.confidence,
              leadTime: p.lead_time
            })))
          }
          setUsingMockData(false)
        }
      } else if (safeAssets.length > 0) {
        // Fallback: generate from asset store using shared cost constants
        // Cost calculation: preventive cost vs reactive/emergency repair cost
        const baseCost = COST_DEFAULTS.DEFAULT_DOWNTIME_COST_PER_HOUR
        const schedule: MaintenanceItem[] = safeAssets
          .filter(a => a.status === 'critical' || a.status === 'warning')
          .slice(0, 5)
          .map((a, i) => ({
            asset: a.id,
            action: a.status === 'critical' ? 'Critical inspection required' : 'Preventive maintenance',
            priority: a.status === 'critical' ? 'critical' : 'high',
            // Estimated preventive maintenance cost (fraction of potential downtime cost)
            cost: a.status === 'critical' ? Math.round(baseCost * 0.5) : Math.round(baseCost * 0.3),
            // Estimated savings = avoided downtime hours × hourly cost × reactive multiplier
            savings: a.status === 'critical'
              ? Math.round(8 * baseCost * COST_DEFAULTS.EMERGENCY_MAINTENANCE_MULTIPLIER)
              : Math.round(4 * baseCost * COST_DEFAULTS.REACTIVE_MAINTENANCE_MULTIPLIER),
            date: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : 'This week'
          }))
        if (schedule.length > 0) {
          setMaintenanceSchedule(schedule)
        } else {
          setMaintenanceSchedule(defaultMaintenanceSchedule)
          setUsingMockData(true)
        }
      } else {
        setMaintenanceSchedule(defaultMaintenanceSchedule)
        setUsingMockData(true)
      }

      // Process efficiency trend data
      if (efficiencyResult.status === 'fulfilled') {
        setEfficiencyTrend(efficiencyResult.value.data)
        if (efficiencyResult.value.isMock) {
          setUsingMockData(true)
        }
      }
    } catch (error) {
      console.warn('Maintenance data API failed, using mock data:', error)
      setMaintenanceSchedule(defaultMaintenanceSchedule)
      setPartsPrediction(defaultPartsPrediction)
      setUsingMockData(true)
    } finally {
      setLoading(false)
    }
  }, [safeAssets])

  // Track if initial data fetch has been done
  const [initialFetchDone, setInitialFetchDone] = useState(false)

  // Fetch assets on mount
  useEffect(() => {
    fetchAssets()
  }, [fetchAssets])

  // Fetch maintenance data when assets are ready or after timeout
  useEffect(() => {
    if (initialFetchDone) return

    // If assets are loaded, fetch immediately
    if (safeAssets.length > 0) {
      fetchMaintenanceData()
      setInitialFetchDone(true)
      return
    }

    // Fallback: fetch after timeout if assets don't load (API-first approach)
    const timer = setTimeout(() => {
      if (!initialFetchDone) {
        fetchMaintenanceData()
        setInitialFetchDone(true)
      }
    }, 1500)

    return () => clearTimeout(timer)
  }, [safeAssets.length, fetchMaintenanceData, initialFetchDone])

  const handleRefresh = () => {
    fetchMaintenanceData()
    addNotification({
      type: 'info',
      title: 'Refreshing',
      message: 'Fetching latest maintenance data...'
    })
  }

  const handleScheduleMaintenance = (asset: string, action: string) => {
    setScheduledItems(prev => new Set([...prev, asset]))
    addNotification({
      type: 'success',
      title: 'Maintenance Scheduled',
      message: `${action} for ${asset} has been added to the schedule`
    })
  }

  const handleAssetClick = (asset: string) => {
    navigate(`/assets/${asset}`)
  }

  // Generate enhanced maintenance narrative with technical facts and actionable insights
  const maintenanceNarrative = useMemo(() => {
    const criticalItems = maintenanceSchedule.filter(m => m.priority === 'critical')
    const highItems = maintenanceSchedule.filter(m => m.priority === 'high')
    const pendingCount = maintenanceSchedule.length
    const roi = totalSavings > 0 ? Math.round((totalSavings / totalCost) * 100) / 100 : 0

    // Get parts insights
    const highUrgencyParts = partsPrediction.filter(p => p.urgency === 'high')
    const totalPartsValue = partsPrediction.reduce((sum, p) => sum + (p.totalCost || 0), 0)

    // Build dynamic narrative based on current state
    let narrative = `**Maintenance Optimization Report**\n\n`

    // Critical Alert Section
    if (criticalItems.length > 0) {
      narrative += `⚠️ **CRITICAL ACTIONS REQUIRED**\n`
      narrative += `${criticalItems.length} asset${criticalItems.length > 1 ? 's require' : ' requires'} immediate attention:\n`
      criticalItems.forEach(item => {
        narrative += `• **${item.asset}**: ${item.action} - Est. downtime prevention: $${item.savings.toLocaleString()}\n`
      })
      narrative += `\n_Failure to address within 24-48 hours may result in unplanned downtime and cascading failures._\n\n`
    }

    // Proactive Maintenance Section
    if (highItems.length > 0) {
      narrative += `📋 **Proactive Maintenance Queue** (${highItems.length} items)\n`
      highItems.slice(0, 3).forEach(item => {
        narrative += `• ${item.asset}: ${item.action} scheduled for ${item.date}\n`
      })
      narrative += `\n`
    }

    // Technical Analysis
    narrative += `📊 **Technical Analysis**\n`
    narrative += `• Total pending actions: ${pendingCount} across ${new Set(maintenanceSchedule.map(m => m.asset)).size} assets\n`
    narrative += `• Estimated investment: $${totalCost.toLocaleString()}\n`
    narrative += `• Projected savings: $${totalSavings.toLocaleString()} (${roi}:1 ROI)\n`

    // Parts Procurement Alert
    if (highUrgencyParts.length > 0) {
      narrative += `\n🔧 **Parts Procurement Alert**\n`
      narrative += `${highUrgencyParts.length} high-urgency part${highUrgencyParts.length > 1 ? 's' : ''} requiring immediate order:\n`
      highUrgencyParts.forEach(p => {
        narrative += `• ${p.part} (×${p.quantity}) - ${p.confidence}% confidence, lead time: ${p.leadTime}\n`
      })
      narrative += `_Total parts value: $${totalPartsValue.toLocaleString()}_\n`
    }

    // Efficiency Trend Insight
    if (efficiencyTrend?.summary) {
      narrative += `\n📈 **Efficiency Trend**\n`
      const trend = efficiencyTrend.summary.trend_direction
      const improvement = efficiencyTrend.summary.improvement_points
      if (trend === 'improving') {
        narrative += `Maintenance efficiency improving: +${improvement.toFixed(1)} points over last 6 months. Current preventive/reactive ratio indicates optimal scheduling.\n`
      } else if (trend === 'declining') {
        narrative += `⚡ Efficiency declining: -${Math.abs(improvement).toFixed(1)} points. Consider reviewing maintenance scheduling strategy and resource allocation.\n`
      } else {
        narrative += `Efficiency stable. Maintain current protocols and monitor for seasonal variations.\n`
      }
    }

    // Protective Recommendation
    narrative += `\n✅ **Recommended Actions**\n`
    if (criticalItems.length > 0) {
      narrative += `1. Prioritize ${criticalItems[0].asset} ${criticalItems[0].action.toLowerCase()} - highest failure probability\n`
      narrative += `2. Pre-position spare parts for rapid response\n`
      narrative += `3. Alert field technicians and prepare work orders\n`
    } else {
      narrative += `1. Continue scheduled preventive maintenance\n`
      narrative += `2. Monitor sensor trends for emerging patterns\n`
      narrative += `3. Review parts inventory levels weekly\n`
    }

    return narrative
  }, [maintenanceSchedule, partsPrediction, totalCost, totalSavings, efficiencyTrend, criticalCount])

  return (
    <PageContainer
      title="Maintenance Intelligence"
      subtitle="AI-optimized maintenance scheduling and cost analysis"
      actions={
        <div className="flex items-center gap-2">
          {usingMockData && (
            <span className="px-2 py-1 text-xs bg-status-warning/20 text-status-warning rounded">
              Mock Data
            </span>
          )}
          <button
            onClick={handleRefresh}
            className="btn btn-secondary flex items-center gap-2"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={() => setShowCostCalculator(!showCostCalculator)}
            className={clsx(
              'btn flex items-center gap-2',
              showCostCalculator ? 'btn-primary' : 'btn-secondary'
            )}
          >
            <Calculator size={16} />
            {showCostCalculator ? 'Hide' : 'Show'} Cost Calculator
          </button>
        </div>
      }
    >
      {/* Smart Compressor Cost Calculator */}
      {showCostCalculator && (
        <div className="mb-6">
          <SmartCompressorCostCalculator
            assetId="F67503"
            compressorModel="Ariel JGJ/2"
            operatingHours={45000}
          />
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center p-8">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-surface-400">Loading maintenance data...</span>
        </div>
      )}

      {/* Summary Metrics */}
      {!loading && (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <MetricCard
          label="Pending Actions"
          value={String(maintenanceSchedule.length)}
          status="warning"
          description="Total maintenance actions awaiting scheduling or completion. Actions are prioritized by AI based on failure risk."
          helpText="Review each action's priority. Critical items should be addressed within 24 hours."
        />
        <MetricCard
          label="Critical Items"
          value={String(criticalCount)}
          status={criticalCount > 0 ? "critical" : "healthy"}
          description="Maintenance actions requiring immediate attention. AI predicts potential failure if not addressed promptly."
          helpText="Schedule critical maintenance immediately. These items have the highest risk of causing unplanned downtime."
        />
        <MetricCard
          label="Proactive Cost"
          value={`$${(totalCost / 1000).toFixed(1)}K`}
          status="neutral"
          description="Total estimated cost for all recommended preventive maintenance actions."
          helpText="This represents the investment needed to prevent potential failures. Compare against prevented losses for ROI."
        />
        <MetricCard
          label="Prevented Losses"
          value={`$${(totalSavings / 1000).toFixed(0)}K`}
          status="healthy"
          description="Estimated value of downtime and repairs avoided by completing proactive maintenance."
          helpText="Calculated based on historical failure costs, production value, and repair expenses for similar failures."
        />
        <MetricCard
          label="ROI"
          value={`${(totalSavings / totalCost).toFixed(1)}x`}
          status="healthy"
          trend="up"
          trendValue={0.3}
          description="Return on Investment - ratio of prevented losses to proactive maintenance cost."
          helpText="ROI above 2x indicates strong value from predictive maintenance. Industry benchmark is 3-5x."
        />
      </div>
      )}

      {!loading && (
      <div className="grid grid-cols-12 gap-4">
        {/* AI Insight */}
        <div className="col-span-12 lg:col-span-6">
          <AIInsightPanel
            type="recommendation"
            title="Maintenance Optimization Report"
            content={maintenanceNarrative}
            confidence={(partsPredictionData?.predictions?.length ?? 0) > 0 ? Math.max(...partsPredictionData!.predictions.map(p => p.confidence)) / 100 : 0.89}
            modelType="ensemble"
            dataSource={partsPredictionData?.is_from_registry ? "Asset registry, sensor data, and failure patterns" : "Asset sensor data, historical maintenance records, and failure patterns"}
            showModelInfo={true}
            helpText="This report prioritizes maintenance actions based on predicted failure probability, cost-benefit analysis, and resource availability. Click the confidence badge for more details about how predictions are made."
          />
        </div>

        {/* Cost-Benefit Chart */}
        <div className="col-span-12 lg:col-span-6">
          <Card title="Cost-Benefit Analysis" subtitle="Proactive vs Reactive maintenance">
            <div className="flex items-center justify-around py-8">
              <div className="text-center">
                <div className="text-3xl font-mono font-bold text-status-healthy">
                  ${(totalCost / 1000).toFixed(1)}K
                </div>
                <div className="text-sm text-surface-400 mt-1">Proactive Cost</div>
              </div>
              <div className="text-4xl text-surface-600">→</div>
              <div className="text-center">
                <div className="text-3xl font-mono font-bold text-status-critical">
                  ${(totalSavings / 1000).toFixed(0)}K
                </div>
                <div className="text-sm text-surface-400 mt-1">Prevented Losses</div>
              </div>
              <div className="text-4xl text-surface-600">=</div>
              <div className="text-center">
                <div className="text-3xl font-mono font-bold text-primary-400">
                  ${((totalSavings - totalCost) / 1000).toFixed(1)}K
                </div>
                <div className="text-sm text-surface-400 mt-1">Net Benefit</div>
              </div>
            </div>
            <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-status-healthy to-primary-500"
                style={{ width: `${(totalCost / totalSavings) * 100}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-surface-500">
              <span>Investment: {((totalCost / totalSavings) * 100).toFixed(0)}%</span>
              <span>Return: {(totalSavings / totalCost).toFixed(1)}x ROI</span>
            </div>
          </Card>
        </div>

        {/* Maintenance Schedule Table */}
        <div className="col-span-12">
          <Card title="Recommended Maintenance Schedule" subtitle="AI-prioritized actions">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-700">
                    <th className="text-left text-xs font-semibold text-surface-400 uppercase py-3 px-4">Priority</th>
                    <th className="text-left text-xs font-semibold text-surface-400 uppercase py-3 px-4">Asset</th>
                    <th className="text-left text-xs font-semibold text-surface-400 uppercase py-3 px-4">Action</th>
                    <th className="text-right text-xs font-semibold text-surface-400 uppercase py-3 px-4">Est. Cost</th>
                    <th className="text-right text-xs font-semibold text-surface-400 uppercase py-3 px-4">Prevented Loss</th>
                    <th className="text-left text-xs font-semibold text-surface-400 uppercase py-3 px-4">Deadline</th>
                    <th className="text-right text-xs font-semibold text-surface-400 uppercase py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {maintenanceSchedule.map((item, i) => (
                    <tr key={i} className="border-b border-surface-800 hover:bg-surface-800/50">
                      <td className="py-3 px-4">
                        <StatusBadge
                          status={item.priority === 'critical' ? 'critical' : item.priority === 'high' ? 'warning' : 'info'}
                          label={item.priority}
                          size="sm"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleAssetClick(item.asset)}
                          className="font-mono text-primary-400 hover:text-primary-300 hover:underline"
                        >
                          {item.asset}
                        </button>
                      </td>
                      <td className="py-3 px-4 text-surface-200">{item.action}</td>
                      <td className="py-3 px-4 text-right font-mono text-surface-300">
                        ${item.cost.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-status-healthy">
                        ${item.savings.toLocaleString()}
                      </td>
                      <td className={clsx(
                        'py-3 px-4',
                        item.date === 'Today' ? 'text-status-critical font-medium' :
                        item.date === 'Tomorrow' ? 'text-status-warning' :
                        'text-surface-400'
                      )}>
                        {item.date}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <ActionButton
                          variant={scheduledItems.has(item.asset) ? 'secondary' : 'primary'}
                          size="sm"
                          onClick={() => handleScheduleMaintenance(item.asset, item.action)}
                          disabled={scheduledItems.has(item.asset)}
                        >
                          {scheduledItems.has(item.asset) ? 'Scheduled' : 'Schedule'}
                        </ActionButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Parts Prediction */}
        <div className="col-span-12 lg:col-span-6">
          <Card
            title="Parts Prediction"
            subtitle={partsPredictionData?.summary ?
              `${partsPredictionData.summary.total_parts_needed} parts needed • $${(partsPredictionData.summary.total_estimated_cost / 1000).toFixed(1)}K est.` :
              "Recommended inventory based on patterns"
            }
          >
            <div className="space-y-3">
              {partsPrediction.map((part, i) => (
                <div key={i} className={clsx(
                  "p-3 rounded-lg border",
                  part.urgency === 'high' ? 'bg-red-500/5 border-red-500/20' :
                  part.urgency === 'medium' ? 'bg-amber-500/5 border-amber-500/20' :
                  'bg-surface-900 border-surface-700'
                )}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-surface-200">{part.part}</p>
                        {part.urgency && (
                          <span className={clsx(
                            "px-1.5 py-0.5 text-[10px] font-medium uppercase rounded",
                            part.urgency === 'high' ? 'bg-red-500/20 text-red-400' :
                            part.urgency === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-surface-700 text-surface-400'
                          )}>
                            {part.urgency}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-surface-500">
                          Lead: <span className="text-surface-400">{part.leadTime}</span>
                        </span>
                        {part.category && (
                          <span className="text-xs text-surface-500">
                            Cat: <span className="text-surface-400 capitalize">{part.category}</span>
                          </span>
                        )}
                        {part.partId && (
                          <span className="text-xs font-mono text-surface-600">{part.partId}</span>
                        )}
                      </div>
                      {part.recommendation && (
                        <p className="mt-2 text-xs text-surface-400 italic">
                          💡 {part.recommendation}
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-3">
                      <p className="text-xl font-mono font-bold text-primary-400">×{part.quantity}</p>
                      {part.totalCost && (
                        <p className="text-sm font-mono text-surface-300">${part.totalCost.toLocaleString()}</p>
                      )}
                      <div className="flex items-center justify-end gap-1 mt-1">
                        <div className="w-12 h-1.5 bg-surface-700 rounded-full overflow-hidden">
                          <div
                            className={clsx(
                              "h-full rounded-full",
                              part.confidence >= 85 ? "bg-emerald-500" :
                              part.confidence >= 70 ? "bg-amber-500" : "bg-red-500"
                            )}
                            style={{ width: `${part.confidence}%` }}
                          />
                        </div>
                        <span className="text-xs text-surface-500">{part.confidence}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Summary Footer */}
            {partsPredictionData?.factors && (
              <div className="mt-4 pt-3 border-t border-surface-700 flex items-center justify-between text-xs text-surface-500">
                <span>Health Factor: <span className="font-mono text-surface-400">{partsPredictionData.factors.health_factor.toFixed(2)}x</span></span>
                <span>Urgency Factor: <span className="font-mono text-surface-400">{partsPredictionData.factors.urgency_factor.toFixed(2)}x</span></span>
                <span>Anomaly Ratio: <span className="font-mono text-surface-400">{(partsPredictionData.factors.avg_anomaly_ratio * 100).toFixed(0)}%</span></span>
              </div>
            )}
          </Card>
        </div>

        {/* Historical Maintenance Trends */}
        <div className="col-span-12 lg:col-span-6">
          <Card
            title="Maintenance Efficiency Trend"
            subtitle={(efficiencyTrend?.summary?.trend_direction && efficiencyTrend?.summary?.improvement_points !== undefined) ?
              `${efficiencyTrend.summary.trend_direction === 'improving' ? '↑' : efficiencyTrend.summary.trend_direction === 'declining' ? '↓' : '→'} ${Math.abs(efficiencyTrend.summary.improvement_points)} pts ${efficiencyTrend.summary.trend_direction}` :
              'Proactive vs Reactive over time'
            }
          >
            <TimeSeriesChart
              data={(efficiencyTrend?.months && efficiencyTrend?.proactive_percent && efficiencyTrend?.reactive_percent) ? [
                {
                  x: efficiencyTrend.months,
                  y: efficiencyTrend.proactive_percent,
                  name: 'Proactive %',
                  color: '#10b981'
                },
                {
                  x: efficiencyTrend.months,
                  y: efficiencyTrend.reactive_percent,
                  name: 'Reactive %',
                  color: '#ef4444'
                }
              ] : [
                {
                  x: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
                  y: [65, 68, 72, 78, 82, 85],
                  name: 'Proactive %',
                  color: '#10b981'
                },
                {
                  x: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
                  y: [35, 32, 28, 22, 18, 15],
                  name: 'Reactive %',
                  color: '#ef4444'
                }
              ]}
              height={250}
              yAxisTitle="Percentage"
            />
            {efficiencyTrend?.benchmarks && efficiencyTrend?.summary?.current_proactive_rate !== undefined && (
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-surface-700 text-xs">
                <span className="text-surface-500">
                  Current: <span className="font-mono text-surface-300">{efficiencyTrend.summary.current_proactive_rate}%</span>
                </span>
                <span className="text-surface-500">
                  Industry Avg: <span className="font-mono text-surface-400">{efficiencyTrend.benchmarks.industry_average ?? 'N/A'}%</span>
                </span>
                <span className="text-surface-500">
                  Best-in-Class: <span className="font-mono text-status-healthy">{efficiencyTrend.benchmarks.best_in_class ?? 'N/A'}%</span>
                </span>
              </div>
            )}
          </Card>
        </div>
      </div>
      )}
    </PageContainer>
  )
}
